// MapView.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Form, Spinner, Alert, Button } from 'react-bootstrap';
import { driverAPI } from '../../api/apiSystem';
import { useTranslation } from 'react-i18next';
import { FaRocket, FaBox, FaCouch, FaCar, FaFilter } from 'react-icons/fa';
import '../../assets/css/ViewMap.css';
import NavbarMenu from '../heroSection/NavbarMenu';
import ClientChatBox from "../chat/ClientChatBox";

// Custom hook for loading Google Maps
const useGoogleMaps = () => {
  const [loaded, setLoaded] = useState(!!window.google?.maps);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loaded) return;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD1U0djKe-DQ9ldXwK8Ob-EiiHcxvRH_3Y&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => setLoaded(true);
    script.onerror = () => setError('Failed to load Google Maps');

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [loaded]);

  return { loaded, error };
};

const MapView = () => {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [currentType, setCurrentType] = useState('regular');
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const { loaded: googleMapsLoaded, error: googleError } = useGoogleMaps();

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null
  );

  useEffect(() => {
    if (googleError) setError(googleError);
  }, [googleError]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      const params = { per_page: 100 };

      switch (currentType) {
        case 'regular':
          response = await driverAPI.bookings.allRegular({});
          break;
        case 'fast':
          response = await driverAPI.bookings.allFast({});
          break;
        case 'cars':
          response = await driverAPI.transports.allCars({});
          break;
        case 'furnitures':
          response = await driverAPI.transports.allFurnitures({});
          break;
        default:
          response = await driverAPI.bookings.allRegular({});
      }

      if (response && response.success) {
        const bookingsData = Array.isArray(response.data.data) ? response.data.data : [];
        setBookings(bookingsData);
        if (googleMapsLoaded) {
          initializeMap(bookingsData);
        }
      } else {
        throw new Error(response?.message || 'Failed to fetch bookings');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentType, googleMapsLoaded]);

  const isTransportType = (type) => {
    return type === 'cars' || type === 'furnitures';
  };

  const isBookingType = (type) => {
    return type === 'regular' || type === 'fast';
  };

  const initializeMap = useCallback((bookingsData) => {
    if (!window.google || !mapRef.current) return setError('Google Maps not available');

    try {
      const mapOptions = {
        zoom: 5.8,
        center: new window.google.maps.LatLng(51.1657, 10.4515),
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'on' }] }],
      };

      mapInstance.current = new window.google.maps.Map(mapRef.current, mapOptions);
      infoWindowRef.current = new window.google.maps.InfoWindow();
      markersRef.current = [];
      addMarkersToMap(bookingsData);
      setMapLoading(false);
    } catch (err) {
      setError('Error initializing map');
    }
  }, []);

  const addMarkersToMap = useCallback((bookingsData) => {
    const geocoder = new window.google.maps.Geocoder();
    bookingsData.forEach((booking) => {
      const address = getBookingAddress(booking);
      if (!address) return;

      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const marker = new window.google.maps.Marker({
            position: location,
            map: mapInstance.current,
            title: getMarkerTitle(booking),
            icon: getMarkerIcon(currentType),
            bookingData: booking,
          });

          marker.bookingDate = getBookingDate(booking);
          markersRef.current.push(marker);

          marker.addListener('click', () => showInfoWindow(marker, booking));
        }
      });
    });
  }, [currentType]);

  const getBookingAddress = (booking) => {
    if (isTransportType(currentType)) {
      return booking.booking_from;
    } else {
      return Array.isArray(booking.booking_from) ? booking.booking_from[0] : booking.booking_from;
    }
  };

  const getMarkerTitle = (booking) => {
    return currentType === 'cars' || currentType === 'furnitures'
      ? `Transport: ${booking.booking_id || booking.id}`
      : `Booking: ${booking.booking_id || booking.id}`;
  };

  const getMarkerIcon = (type) => {
    const baseUrl = 'https://maps.google.com/mapfiles/ms/icons/';
    const colors = {
      regular: 'blue',
      fast: 'red',
      cars: 'green',
      furnitures: 'purple',
    };
    return {
      url: `${baseUrl}${colors[type] || 'blue'}-dot.png`,
      scaledSize: new window.google.maps.Size(32, 32),
    };
  };

  const getBookingDate = (booking) => {
    if (isTransportType(currentType)) {
      return booking.loading_date ? booking.loading_date.split(' - ')[0] : '';
    } else {
      const datetime = booking.loading_date_time || [];
      return Array.isArray(datetime) ? datetime[0]?.split(' ')[0] : '';
    }
  };

  const showInfoWindow = (marker, booking) => {
    const content = generateInfoWindowContent(booking);
    infoWindowRef.current.setContent(content);
    infoWindowRef.current.open(mapInstance.current, marker);
  };

  const generateInfoWindowContent = (booking) => {
    console.log("type", currentType);
    let content = currentType === 'cars' || currentType === 'furnitures'
      ? generateTransportContent(booking)
      : generateBookingContent(booking);
    const detailsUrl = generateDetailsUrl(booking);
    return `
      <div class="map-info-window">
        ${content}
        <div class="text-center mt-3">
          <a href="${detailsUrl}" class="btn btn-primary btn-sm" target="_blank">
            ${t('driverCard.general.showDetails')}
          </a>
        </div>
      </div>
    `;
  };

  const generateBookingContent = (booking) => {
    const totalAmount = parseFloat(booking.sub_total || booking.price || 0);
    const discountPercentage = parseFloat(booking.discount_percentage || 0);
    const discountedAmount = totalAmount * (discountPercentage / 100);
    const totalAfterDiscount = totalAmount - discountedAmount;

    const [loadingDate, loadingTime] = (booking.loading_date_time || [])[0]?.split(' ') || ['N/A', ''];
    const [unloadingDate, unloadingTime] = (booking.unloading_date_time || [])[0]?.split(' ') || ['N/A', ''];

    const fromAddress = Array.isArray(booking.booking_from) ? booking.booking_from[0] : booking.booking_from || 'N/A';
    const toAddress = Array.isArray(booking.booking_to) ? booking.booking_to[0] : booking.booking_to || 'N/A';

    return `
      <p><strong>${t('driverCard.general.pickup')}:</strong> ${fromAddress}</p>
      <p>${loadingDate} ${loadingTime}</p>
      <hr>
      <p><strong>${t('driverCard.general.delivery')}:</strong> ${toAddress}</p>
      <p>${unloadingDate} ${unloadingTime}</p>
      <hr>
      <p><strong>${t('driverCard.orderDetails.vehicleType')}:</strong> ${booking.vehicle_type || 'N/A'}</p>
      <p><strong>${t('driverCard.general.route')}:</strong> ${(booking.distance || '0').replace('km', ' km').replace('KM', ' KM')} KM</p>
      <p><strong>${t('driverCard.general.weight')}:</strong> ${booking.total_weight || '0'} Kg</p>
      <hr>
      <p><strong>${t('driverCard.priceDetails.priceSuggestion')}:</strong> ${booking.discounted_price} EUR</p>
    `;
  };

  const generateTransportContent = (transport) => {
    const itemTypes = Array.isArray(transport.item_type) ? transport.item_type.join(', ') : 'N/A';

    return `
      <p><strong>${t('driverCard.transportDetails.transportType')}:</strong> ${t(transport.transport_type) || 'N/A'}</p>
      <hr>
      <p><strong>${t('driverCard.general.pickup')}:</strong> ${transport.booking_from || 'N/A'}</p>
      <p>${transport.loading_date || 'N/A'}</p>
      <hr>
      <p><strong>${t('driverCard.general.delivery')}:</strong> ${transport.booking_to || 'N/A'}</p>
      <p>${transport.unloading_date || 'N/A'}</p>
      <hr>
      <p><strong>${t('driverCard.transportDetails.items')}:</strong> ${itemTypes}</p>
      <p><strong>${t('driverCard.general.route')}:</strong> ${(transport.distance || '0').replace('km', ' km').replace('KM', ' KM')} KM</p>
      <p><strong>${t('driverCard.general.weight')}:</strong> ${transport.total_weight || '0'} Kg</p>
    `;
  };

  const generateDetailsUrl = (booking) => {
    let type;
    
    if (booking.transport_type) {
      if (booking.transport_type === 'car') {
        type = 'cars';
      } else {
        type = 'furnitures';
      }
    } 

    else if (booking.fast_order === 1) {
      type = 'fast';
    } 
    else {
      type = 'regular';
    }
    
    const param = type === 'cars' || type === 'furnitures' ? 'transport_id' : 'booking_id';
    const id = booking.booking_id || booking.id;
    
    console.log('🔗 Generated URL:', { 
      id, 
      type, 
      param,
      transportType: booking.transport_type,
      fastOrder: booking.fast_order,
      itemTypes: booking.item_type 
    });
    
    return `/driver/driver-details?${param}=${id}&type=${type}&open_details=true`;
  };

  const filterMarkersByDate = useCallback(() => {
    if (!selectedDate) return;
    const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
    markersRef.current.forEach((marker) => {
      marker.setVisible(marker.bookingDate === formattedDate);
    });
  }, [selectedDate]);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
  }, []);

  const reloadMap = useCallback(() => {
    setMapLoading(true);
    clearMarkers();
    fetchBookings();
  }, [fetchBookings, clearMarkers]);

  const handleTypeChange = useCallback((type) => {
    setCurrentType(type);
    clearMarkers();
  }, [clearMarkers]);

  useEffect(() => {
    if (googleMapsLoaded) fetchBookings();
  }, [currentType, googleMapsLoaded, fetchBookings]);

  useEffect(() => {
    filterMarkersByDate();
  }, [selectedDate, filterMarkersByDate]);

  useEffect(() => {
    return () => clearMarkers(); // Cleanup on unmount
  }, [clearMarkers]);

  return (
    <>
      <NavbarMenu />
      <div className="contact-page px-lg-0">
        <Container fluid>
          <Row className="m-0 mx-lg-0">
            <Col md={12}>
              <div className="mb-5">
                <Row className="justify-content-center">
                  <div className="w-100">
                    {/* <h1 className="text-center mt-2 mt-md-5 mb-1 title22">
                      {t('mapView.transportMap')}
                    </h1> */}
                    
                    {error && (
                      <Alert variant="danger" className="mt-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{error}</span>
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={reloadMap}
                          >
                            {t('mapView.retry')}
                          </button>
                        </div>
                      </Alert>
                    )}

                    <Row className="text-center my-5">
                      <Col>
                        <div className="d-flex justify-content-center gap-3 fw-bold fs-6 flex-wrap">
                          <Button
                            className={currentType === "fast" ? "bg-dark text-white active" : "bg-orange"}
                            onClick={() => handleTypeChange("fast")}
                            disabled={loading}
                          >
                            <FaRocket className="me-2" />
                            {t('driverCard.orderTypes.fast')}
                          </Button>
                          <Button
                            className={currentType === "regular" ? "bg-dark text-white active" : "bg-orange"}
                            onClick={() => handleTypeChange("regular")}
                            disabled={loading}
                          >
                            <FaBox className="me-2" />
                            {t('driverCard.orderTypes.regular')}
                          </Button>
                          <Button
                            className={currentType === "furnitures" ? "bg-dark text-white active" : "bg-orange"}
                            onClick={() => handleTypeChange("furnitures")}
                            disabled={loading}
                          >
                            <FaCouch className="me-2" />
                            {t('driverCard.orderTypes.furniture')}
                          </Button>
                          <Button
                            className={currentType === "cars" ? "bg-dark text-white active" : "bg-orange"}
                            onClick={() => handleTypeChange("cars")}
                            disabled={loading}
                          >
                            <FaCar className="me-2" />
                            {t('driverCard.orderTypes.car')}
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col>
                        <div className="stats-info text-center">
                          <span className="badge bg-primary">
                            {t('driverCard.transportDetails.items')}: {bookings.length} {t(currentType)}
                          </span>
                        </div>
                      </Col>
                    </Row>

                    <section className="content position-relative">
                      {(loading || mapLoading) && (
                        <div className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2">{t('mapView.loading')}</p>
                        </div>
                      )}
                      
                      <div 
                        ref={mapRef} 
                        id="map" 
                        className="map-container"
                        style={{ 
                          opacity: (loading || mapLoading) ? 0.5 : 1,
                          transition: 'opacity 0.3s ease'
                        }}
                      />
                    </section>
                  </div>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
};

export default MapView;